/**
 * Single source of truth for "can a coach register for this event right now?".
 *
 * Precedence (applied in this order):
 *   1. external_registration_url  → registration happens off site. Set this and
 *      registration always sends the coach to the external URL, whatever the
 *      other flags say. (Organisers pair it with accept_online_registrations =
 *      false, so it must be evaluated before that flag or off-site events would
 *      read as closed.)
 *   2. accept_online_registrations === false → CLOSED. Overrides the date window.
 *   3. registration_open_date in the future → NOT OPEN YET.
 *   4. registration_close_date in the past → CLOSED.
 *   5. otherwise OPEN.
 */

export type RegistrationAvailability =
  | { state: "open"; label: string; message: null; externalUrl: null }
  | { state: "external"; label: string; message: string; externalUrl: string }
  | { state: "not_open_yet"; label: string; message: string; externalUrl: null }
  | { state: "closed"; label: string; message: string; externalUrl: null };

export interface RegistrationAvailabilityEvent {
  name?: string | null;
  accept_online_registrations?: boolean | null;
  external_registration_url?: string | null;
  registration_open_date?: string | null;
  registration_close_date?: string | null;
}

/** Calendar-date comparison — never construct a Date from a raw column. */
function dayNumber(value: string): number {
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return NaN;
  return Date.UTC(y, m - 1, d);
}

function todayNumber(): number {
  const now = new Date();
  return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
}

export function getRegistrationAvailability(
  event: RegistrationAvailabilityEvent | null | undefined
): RegistrationAvailability {
  if (!event) {
    return {
      state: "closed",
      label: "Registration Closed",
      message: "Registration is not open for this event.",
      externalUrl: null,
    };
  }

  const external = event.external_registration_url?.trim();
  if (external) {
    return {
      state: "external",
      label: "Register (external site)",
      message: "Registration for this event is handled on another site.",
      externalUrl: external,
    };
  }

  if (event.accept_online_registrations === false) {
    return {
      state: "closed",
      label: "Registration Closed",
      message: "Registration is not open for this event.",
      externalUrl: null,
    };
  }

  const today = todayNumber();

  const opens = event.registration_open_date ? dayNumber(event.registration_open_date) : NaN;
  if (!Number.isNaN(opens) && opens > today) {
    return {
      state: "not_open_yet",
      label: "Registration Not Open Yet",
      message: "Registration for this event hasn't opened yet.",
      externalUrl: null,
    };
  }

  const closes = event.registration_close_date ? dayNumber(event.registration_close_date) : NaN;
  if (!Number.isNaN(closes) && closes < today) {
    return {
      state: "closed",
      label: "Registration Closed",
      message: "Registration for this event has closed.",
      externalUrl: null,
    };
  }

  return { state: "open", label: "Register Team", message: null, externalUrl: null };
}

export function isRegistrationBlocked(a: RegistrationAvailability): boolean {
  return a.state === "closed" || a.state === "not_open_yet";
}

/** Columns every caller must select for the helper to be accurate. */
export const REGISTRATION_AVAILABILITY_FIELDS =
  "accept_online_registrations,external_registration_url,registration_open_date,registration_close_date";
